/**
 * SecretsPanel — Admin console for managing app secrets in DB.
 * Allows the admin to set RESEND_API_KEY, RAZORPAY_WEBHOOK_SECRET, etc.
 * directly from the UI when the hosting environment doesn't expose env-var
 * editing. DB values override env values at runtime.
 */
import { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Lock, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const sourceBadge = (src, configured) => {
  if (!configured) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Not set</span>;
  }
  if (src === "db") return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">DB</span>;
  if (src === "env") return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Env</span>;
  return null;
};

export default function SecretsPanel() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [draftKey, setDraftKey] = useState({});  // {KEY: "new-value-string"}
  const [revealed, setRevealed] = useState({});  // {KEY: bool}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/secrets");
      setItems(data.items || []);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by, note: data.note });
    } catch (e) {
      toast.error("Failed to load secrets: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = (draftKey[key] || "").trim();
    if (!value) { toast.error("Enter a value first"); return; }
    try {
      await api.put("/admin/secrets", { key, value });
      toast.success(`${key} updated. Effective immediately.`);
      setDraftKey({ ...draftKey, [key]: "" });
      setRevealed({ ...revealed, [key]: false });
      load();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Remove ${key} from DB? Will fall back to environment variable if set.`)) return;
    try {
      await api.delete(`/admin/secrets/${key}`);
      toast.success(`${key} cleared from DB`);
      load();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="admin-secrets-panel" className="space-y-6">
      <div className="card-flat p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={18} className="text-[#0B7B6E]" />
              <h2 className="font-serif text-2xl">API Keys &amp; Secrets</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Set production secrets directly from this console. <strong>DB values override
              environment variables</strong>. No restart needed — changes take effect on the next
              request. Sensitive values are masked when read back.
            </p>
            {meta.updated_at && (
              <p className="text-xs text-muted-foreground mt-2">
                Last change: {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || "—"}
              </p>
            )}
          </div>
          <Button
            data-testid="reload-secrets-btn"
            variant="outline"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Reload"}
          </Button>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-4 flex gap-2 items-start">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800">
            Secrets are stored in MongoDB collection <code className="px-1 bg-amber-100 rounded">app_secrets</code>.
            Anyone with database access can read them — they are <strong>not</strong> encrypted at rest.
            Use this only when you can&apos;t set env vars in your hosting environment.
          </div>
        </div>

        <div className="space-y-3">
          {items.map((it) => {
            const isRevealed = !!revealed[it.key];
            const draft = draftKey[it.key] ?? "";
            return (
              <div
                key={it.key}
                data-testid={`secret-row-${it.key}`}
                className="rounded-md border border-[#e5e1d6] bg-white p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono text-sm font-semibold">{it.key}</code>
                      {sourceBadge(it.source, it.configured)}
                      {it.set_in_env && it.set_in_db && (
                        <span className="text-xs text-muted-foreground">(DB overriding env)</span>
                      )}
                      {it.configured && <CheckCircle2 size={14} className="text-emerald-600" />}
                    </div>
                    <div className="text-sm text-foreground mt-0.5">{it.label}</div>
                    <div className="text-xs text-muted-foreground">{it.purpose}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center mt-3">
                  <div className="relative">
                    <Input
                      data-testid={`secret-input-${it.key}`}
                      type={it.sensitive && !isRevealed ? "password" : "text"}
                      placeholder={it.configured ? it.masked_value : `Enter ${it.label}…`}
                      value={draft}
                      onChange={(e) => setDraftKey({ ...draftKey, [it.key]: e.target.value })}
                      className="font-mono pr-10"
                    />
                    {it.sensitive && (
                      <button
                        type="button"
                        aria-label={isRevealed ? "Hide" : "Reveal"}
                        onClick={() => setRevealed({ ...revealed, [it.key]: !isRevealed })}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {isRevealed ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                  <Button
                    data-testid={`secret-save-${it.key}`}
                    onClick={() => save(it.key)}
                    disabled={!draft.trim()}
                  >
                    {it.set_in_db ? "Update" : "Save"}
                  </Button>
                  {it.set_in_db && (
                    <Button
                      data-testid={`secret-delete-${it.key}`}
                      variant="outline"
                      size="icon"
                      onClick={() => remove(it.key)}
                      title="Remove from DB (will fall back to env)"
                    >
                      <Trash2 size={16} className="text-rose-600" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-flat p-6 text-xs text-muted-foreground">
        <div className="flex items-center gap-2 mb-2 text-foreground">
          <Lock size={14} />
          <span className="font-medium">Security notes</span>
        </div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Values entered here are stored in MongoDB. They are not encrypted at rest — restrict admin access.</li>
          <li>Code reads DB values first; if a key is unset in DB, it falls back to the environment variable.</li>
          <li>Removing a DB value reverts to the env var (if set) or makes the integration a no-op.</li>
          <li>Cache is warmed at startup and refreshed on every Save. No service restart needed.</li>
        </ul>
      </div>
    </div>
  );
}
